import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { RefreshCw, ChevronDown, ChevronUp, BookOpen } from "lucide-react";
import {
  getTelegramConfig,
  updateTelegramConfig,
  deleteTelegramConfig,
  getTelegramWebhookStatus,
  registerTelegramWebhook,
  removeTelegramWebhook,
  readApiError,
} from "./supportApi";
import SupportConfirmationModal from "./SupportConfirmationModal";

export default function TelegramSetup() {
  const [chatId, setChatId] = useState("");
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteWebhookModal, setShowDeleteWebhookModal] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  // Webhook status states
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [isLoadingWebhook, setIsLoadingWebhook] = useState(true);
  const [isRefreshingWebhook, setIsRefreshingWebhook] = useState(false);
  const [isRegisteringWebhook, setIsRegisteringWebhook] = useState(false);
  const [isRemovingWebhook, setIsRemovingWebhook] = useState(false);

  useEffect(() => {
    checkTelegramConfig();
    checkWebhookStatus();
  }, []);

  const checkTelegramConfig = async () => {
    setIsLoading(true);
    try {
      const data = await getTelegramConfig();
      setIsConfigured(data.configured || false);
      setChatId(data.chatId || "");
    } catch (error) {
      console.error("Error checking Telegram config:", error);
      setIsConfigured(false);
      setChatId("");
    } finally {
      setIsLoading(false);
    }
  };

  const checkWebhookStatus = async () => {
    setIsLoadingWebhook(true);
    try {
      const data = await getTelegramWebhookStatus();
      setWebhookStatus(data);
    } catch (error) {
      console.error("Error checking webhook status:", error);
      setWebhookStatus(null);
    } finally {
      setIsLoadingWebhook(false);
    }
  };

  const handleRefreshWebhook = async () => {
    setIsRefreshingWebhook(true);
    try {
      const data = await getTelegramWebhookStatus();
      setWebhookStatus(data);
      toast.success("Webhook status refreshed");
    } catch (error) {
      console.error("Error refreshing webhook status:", error);
      toast.error("Failed to refresh webhook status");
    } finally {
      setIsRefreshingWebhook(false);
    }
  };

  const handleRegisterWebhook = async () => {
    setIsRegisteringWebhook(true);
    try {
      const response = await registerTelegramWebhook();

      if (response.ok || response.status === 200) {
        const data = await response.json();
        toast.success(data.message || "Webhook registered successfully");
        await checkWebhookStatus(); // Refresh webhook status
      } else {
        toast.error(await readApiError(response, "Could not register the webhook"));
      }
    } catch (error) {
      console.error("Error registering webhook:", error);
      toast.error("Failed to register webhook. Please try again.");
    } finally {
      setIsRegisteringWebhook(false);
    }
  };

  const handleRemoveWebhook = async () => {
    setIsRemovingWebhook(true);
    try {
      const response = await removeTelegramWebhook();

      if (response.ok || response.status === 200) {
        const data = await response.json();
        toast.success(data.message || "Webhook removed successfully");
        await checkWebhookStatus(); // Refresh webhook status
      } else {
        toast.error(await readApiError(response, "Could not remove the webhook"));
      }
    } catch (error) {
      console.error("Error removing webhook:", error);
      toast.error("Failed to remove webhook. Please try again.");
    } finally {
      setIsRemovingWebhook(false);
      setShowDeleteWebhookModal(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();

    // Validation
    if (!chatId.trim()) {
      toast.error("Please enter a Chat ID");
      return;
    }

    // Validate numbers only
    if (!/^\d+$/.test(chatId.trim())) {
      toast.error("Chat ID must contain only numbers");
      return;
    }

    setIsSaving(true);
    try {
      const response = await updateTelegramConfig(chatId.trim());

      if (response.ok || response.status === 200) {
        toast.success("Telegram configuration saved successfully");
        await checkTelegramConfig(); // Refresh config
      } else {
        toast.error(await readApiError(response, "Could not save the configuration"));
      }
    } catch (error) {
      console.error("Error saving Telegram config:", error);
      toast.error("Failed to save configuration. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfig = async () => {
    setIsDeleting(true);
    try {
      const response = await deleteTelegramConfig();

      if (response.ok || response.status === 204) {
        toast.success("Telegram configuration removed successfully");
        setChatId("");
        await checkTelegramConfig(); // Refresh config
      } else {
        toast.error(await readApiError(response, "Could not remove the configuration"));
      }
    } catch (error) {
      console.error("Error deleting Telegram config:", error);
      toast.error("Failed to remove configuration. Please try again.");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Support
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Telegram <span className="text-gradient-ginox">Setup</span>
        </h1>
        <p className="text-xs text-muted-foreground">
          Configure Telegram notifications for support agents
        </p>
      </div>

      {/* Setup Guide Card */}
      <div className="rounded-lg border border-border/70 bg-card p-5">
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen size={15} className="text-muted-foreground" aria-hidden="true" />
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Setup Guide
              </p>
            </div>
            <button
              onClick={() => setShowGuide(!showGuide)}
              className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors flex items-center gap-1"
            >
              {showGuide ? (
                <>
                  <ChevronUp size={13} aria-hidden="true" />
                  Hide Guide
                </>
              ) : (
                <>
                  <ChevronDown size={13} aria-hidden="true" />
                  Show Guide
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Follow these steps to set up Telegram notifications
          </p>
        </div>

        {showGuide && (
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full border border-border/70 bg-white/[0.04] flex items-center justify-center font-mono text-xs text-foreground">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-xs font-semibold text-foreground mb-2">
                  Create a Telegram Bot
                </h3>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li>
                    • Open Telegram and search for{" "}
                    <code className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded px-1.5 py-0.5 text-foreground">
                      @BotFather
                    </code>
                  </li>
                  <li>
                    • Send the command{" "}
                    <code className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded px-1.5 py-0.5 text-foreground">
                      /newbot
                    </code>{" "}
                    and follow the prompts
                  </li>
                  <li>• Choose a name and username for your bot</li>
                  <li>
                    • Save the bot token provided (you'll need it for server
                    configuration)
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full border border-border/70 bg-white/[0.04] flex items-center justify-center font-mono text-xs text-foreground">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-xs font-semibold text-foreground mb-2">
                  Get Your Telegram Chat ID
                </h3>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li>• Start a conversation with your bot</li>
                  <li>
                    • Send{" "}
                    <code className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded px-1.5 py-0.5 text-foreground">
                      /start
                    </code>{" "}
                    to the bot
                  </li>
                  <li>• The bot will respond with your chat ID</li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full border border-border/70 bg-white/[0.04] flex items-center justify-center font-mono text-xs text-foreground">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-xs font-semibold text-foreground mb-2">
                  Set Admin Chat ID
                </h3>
                <p className="text-xs text-muted-foreground mb-2">
                  Configure your Telegram account to receive admin notifications:
                </p>
                <pre className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground whitespace-pre-wrap break-all mb-2">
                  {`curl -X PUT https://your-domain.com/api/v1/admin/telegram \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"chatId": "YOUR_CHAT_ID"}'`}
                </pre>
                <p className="text-xs text-muted-foreground mb-1">Response:</p>
                <pre className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground">
                  {`{
  "chatId": "123456789",
  "updatedAt": "2026-01-15T12:00:00.000Z"
}`}
                </pre>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full border border-border/70 bg-white/[0.04] flex items-center justify-center font-mono text-xs text-foreground">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-xs font-semibold text-foreground mb-2">
                  Check Current Configuration
                </h3>
                <pre className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground whitespace-pre-wrap break-all mb-2">
                  {`curl https://your-domain.com/api/v1/admin/telegram \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
                </pre>
                <p className="text-xs text-muted-foreground mb-1">
                  Response when configured:
                </p>
                <pre className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground mb-2">
                  {`{
  "chatId": "123456789",
  "updatedAt": "2026-01-15T12:00:00.000Z"
}`}
                </pre>
                <p className="text-xs text-muted-foreground mb-1">
                  Response when not configured:
                </p>
                <pre className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground">
                  {`{
  "chatId": null,
  "updatedAt": null
}`}
                </pre>
              </div>
            </div>

            {/* Step 5 */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full border border-border/70 bg-white/[0.04] flex items-center justify-center font-mono text-xs text-foreground">
                5
              </div>
              <div className="flex-1">
                <h3 className="text-xs font-semibold text-foreground mb-2">
                  Remove Admin Chat ID
                </h3>
                <pre className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground whitespace-pre-wrap break-all">
                  {`curl -X DELETE https://your-domain.com/api/v1/admin/telegram \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
                </pre>
              </div>
            </div>

            {/* Admin Notifications */}
            <div className="rounded-md border border-border/70 bg-white/[0.02] p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Admin Notifications
              </p>
              <p className="text-xs text-muted-foreground">
                Once configured, admins receive notifications for:
              </p>
              <ul className="space-y-2 text-xs text-muted-foreground">
                <li>
                  • <strong className="text-foreground">New Pending Chat:</strong> Customer chat
                  escalated, needs attention
                </li>
                <li>
                  • <strong className="text-foreground">Chat Claimed:</strong> Agent claimed a pending
                  chat
                </li>
                <li>
                  • <strong className="text-foreground">Chat Available:</strong> Agent unassigned from
                  chat
                </li>
                <li>
                  • <strong className="text-foreground">Chat Closed:</strong> Chat closed (by customer
                  or agent)
                </li>
              </ul>
              <p className="text-xs text-muted-foreground">
                Notification content includes:
              </p>
              <ul className="space-y-1 text-xs text-muted-foreground ml-4">
                <li>• Session ID</li>
                <li>• Timestamp (absolute and relative)</li>
                <li>• Customer name and email</li>
                <li>• Product (if selected)</li>
                <li>• Event-specific details (agent name, closure reason, etc.)</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Configuration Card */}
      <div className="rounded-lg border border-border/70 bg-card p-5">
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              Configuration Status
            </p>
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${
                isConfigured
                  ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/10"
                  : "text-muted-foreground border-border/70 bg-white/[0.03]"
              }`}
            >
              {isConfigured ? "Configured" : "Not Configured"}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Current status of your Telegram integration
          </p>
        </div>

        {isLoading ? (
          <div className="py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green mx-auto" />
          </div>
        ) : (
          <form onSubmit={handleSaveConfig} className="space-y-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Chat ID
              </label>
              <input
                type="text"
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="Enter your Telegram Chat ID"
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                disabled={isSaving || isDeleting}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Enter your Telegram Chat ID to receive notifications
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="submit"
                disabled={isSaving || isDeleting}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Saving..." : "Save Configuration"}
              </button>

              {isConfigured && (
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={isSaving || isDeleting}
                  className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? "Removing..." : "Remove Configuration"}
                </button>
              )}
            </div>
          </form>
        )}
      </div>

      {/* Webhook Status Card */}
      <div className="rounded-lg border border-border/70 bg-card p-5">
        <div className="mb-4">
          <div className="flex flex-col gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Webhook Status
                </p>
                {webhookStatus && (
                  <span
                    className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${
                      webhookStatus.configured
                        ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/10"
                        : "text-muted-foreground border-border/70 bg-white/[0.03]"
                    }`}
                  >
                    {webhookStatus.configured ? "Configured" : "Not Configured"}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Current webhook configuration from Telegram API
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <button
                onClick={handleRegisterWebhook}
                disabled={
                  isRegisteringWebhook ||
                  isRefreshingWebhook ||
                  isRemovingWebhook
                }
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRegisteringWebhook ? "Registering..." : "Register Webhook"}
              </button>
              {webhookStatus?.configured && (
                <button
                  onClick={() => setShowDeleteWebhookModal(true)}
                  disabled={
                    isRegisteringWebhook ||
                    isRefreshingWebhook ||
                    isRemovingWebhook
                  }
                  className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRemovingWebhook ? "Removing..." : "Remove Webhook"}
                </button>
              )}
              <button
                onClick={handleRefreshWebhook}
                disabled={
                  isRefreshingWebhook ||
                  isRegisteringWebhook ||
                  isRemovingWebhook
                }
                className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                <RefreshCw
                  size={13}
                  className={isRefreshingWebhook ? "animate-spin" : ""}
                  aria-hidden="true"
                />
                {isRefreshingWebhook ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {isLoadingWebhook ? (
          <div className="py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green mx-auto" />
          </div>
        ) : webhookStatus ? (
          <div className="space-y-4">
            {webhookStatus.url && (
              <div>
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Webhook URL
                </label>
                <p className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground break-all">
                  {webhookStatus.url}
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Pending Updates
                </label>
                <p className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground">
                  {webhookStatus.pendingUpdateCount ?? 0}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Custom Certificate
                </label>
                <p className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground">
                  {webhookStatus.hasCustomCertificate ? "Yes" : "No"}
                </p>
              </div>
            </div>

            {webhookStatus.lastErrorMessage && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-red-400 mb-1.5">
                  Last Error
                </label>
                <p className="text-xs text-red-400">
                  {webhookStatus.lastErrorMessage}
                </p>
                {webhookStatus.lastErrorDate > 0 && (
                  <p className="font-mono text-[10px] text-red-400/70 mt-1">
                    {new Date(
                      webhookStatus.lastErrorDate * 1000,
                    ).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Failed to load webhook status
          </p>
        )}
      </div>

      {/* Confirmation Modal - Remove Configuration */}
      <SupportConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfig}
        title="Remove Telegram Configuration"
        message="Are you sure you want to remove Telegram configuration? You will stop receiving notifications."
        confirmText="Yes, Remove"
        cancelText="No, Cancel"
        loading={isDeleting}
        loadingText="Removing..."
      />

      {/* Confirmation Modal - Remove Webhook */}
      <SupportConfirmationModal
        isOpen={showDeleteWebhookModal}
        onClose={() => setShowDeleteWebhookModal(false)}
        onConfirm={handleRemoveWebhook}
        title="Remove Webhook"
        message="Are you sure you want to remove the webhook registration from Telegram? You will stop receiving webhook notifications."
        confirmText="Yes, Remove"
        cancelText="No, Cancel"
        loading={isRemovingWebhook}
        loadingText="Removing..."
      />
    </div>
  );
}
